import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Check, Zap, Crown, Rocket, Building2, Gift, ArrowRight } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useMemo, useState } from "react";

/* ─── Subscription Tiers ─── */
const tiers = [
  {
    name: "Free",
    price: "$0",
    period: "",
    icon: Zap,
    color: "text-slate-400",
    borderColor: "border-white/10",
    bgAccent: "bg-white/5",
    description: "Perfect for players and first-time creators",
    gameFee: "$20",
    gameFeeNote: "per game",
    features: [
      "Play unlimited games",
      "Automatic promoter links",
      "Pro-rata promoter earnings",
      "Community voting",
      "Basic game creation",
      "1 free game creation credit",
    ],
    cta: "Get Started Free",
    href: "/games",
    popular: false,
    tier: "free",
  },
  {
    name: "Starter",
    price: "$19",
    period: "/month",
    icon: Crown,
    color: "text-amber-400",
    borderColor: "border-amber-400/30",
    bgAccent: "bg-amber-400/10",
    description: "For active creators running regular competitions",
    gameFee: "$10",
    gameFeeNote: "per game (50% off)",
    features: [
      "Everything in Free",
      "2 free game creations/month",
      "Priority AI game advisor",
      "Advanced analytics dashboard",
      "Custom game branding",
      "Email support",
    ],
    cta: "Start Creating",
    href: "/create",
    popular: false,
    tier: "starter",
  },
  {
    name: "Pro",
    price: "$49",
    period: "/month",
    icon: Rocket,
    color: "text-neon",
    borderColor: "border-neon/30",
    bgAccent: "bg-neon/10",
    description: "For serious creators and influencers building a business",
    gameFee: "$5",
    gameFeeNote: "per game (75% off)",
    features: [
      "Everything in Starter",
      "5 free game creations/month",
      "AI outreach tools for promoter recruitment",
      "Embeddable game widgets",
      "Priority game featuring",
      "Dedicated support",
    ],
    cta: "Go Pro",
    href: "/create",
    popular: true,
    tier: "pro",
  },
  {
    name: "Enterprise",
    price: "$99",
    period: "/month",
    icon: Building2,
    color: "text-violet-400",
    borderColor: "border-violet-400/30",
    bgAccent: "bg-violet-400/10",
    description: "For organizations, brands, and high-volume creators",
    gameFee: "$0",
    gameFeeNote: "unlimited games",
    features: [
      "Everything in Pro",
      "Unlimited free game creations",
      "White-label game pages",
      "API access for automation",
      "Custom prize structures",
      "Dedicated account manager",
    ],
    cta: "Contact Us",
    href: "/create",
    popular: false,
    tier: "enterprise",
  },
] as const;

/* ─── Discount Triggers ─── */
const discountTriggers = [
  {
    trigger: "First Game Free",
    discount: "100% off",
    description: "Your very first game creation is completely free. No credit card required.",
    icon: Gift,
  },
  {
    trigger: "Player → Creator Upgrade",
    discount: "75% off",
    description: "Played a game and want to create your own? Get 75% off your first creation.",
    icon: ArrowRight,
  },
  {
    trigger: "Referral Reward",
    discount: "50% off",
    description: "When someone you referred buys their first ticket, you get 50% off your next game creation.",
    icon: Zap,
  },
  {
    trigger: "Successful Game Bonus",
    discount: "50% off",
    description: "Fill your game to 80%+ capacity and get 50% off your next creation.",
    icon: Crown,
  },
  {
    trigger: "Fast Action (48hr)",
    discount: "75% off",
    description: "Limited-time flash offers sent via email. Act within 48 hours for massive savings.",
    icon: Rocket,
  },
  {
    trigger: "Subscriber Included",
    discount: "Free",
    description: "Starter, Pro, and Enterprise subscribers get free game creations included in their plan.",
    icon: Building2,
  },
];

export default function Pricing() {
  const { isAuthenticated } = useAuth();
  const [checkoutTier, setCheckoutTier] = useState<"starter" | "pro" | "enterprise" | null>(null);
  const catalog = trpc.subscription.catalog.useQuery();
  const createSubscription = trpc.subscription.createSubscriptionSession.useMutation({
    onSuccess: data => window.location.assign(data.checkoutUrl),
    onError: error => {
      setCheckoutTier(null);
      toast.error("Unable to start subscription checkout", { description: error.message });
    },
  });
  const catalogByTier = useMemo(() => new Map((catalog.data ?? []).map(plan => [plan.tier, plan])), [catalog.data]);

  const beginCheckout = (tier: "starter" | "pro" | "enterprise") => {
    if (!isAuthenticated) {
      window.location.assign(getLoginUrl());
      return;
    }
    const plan = catalogByTier.get(tier);
    if (!plan?.available) {
      toast.error("This plan is not configured for checkout yet.");
      return;
    }
    setCheckoutTier(tier);
    createSubscription.mutate({ tier });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicNav />

      {/* Hero */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold uppercase mb-4">
            Simple <span className="text-neon">Pricing</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
            Playing is always free. Creating games starts at $20 per game — or subscribe to save up to 100%.
            Every plan includes unlimited playing, automatic promoter links, and pro-rata earnings.
          </p>
          <p className="text-sm text-muted-foreground">
            Platform fee: 15% of every game's entry pool (covers payment processing, infrastructure, and operations)
          </p>
        </div>
      </section>

      {/* Subscription Tiers */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier) => (
            (() => {
              const stripePlan = tier.tier === "free" ? null : catalogByTier.get(tier.tier);
              const displayPrice = stripePlan?.available && stripePlan.priceCents != null
                ? `$${(stripePlan.priceCents / 100).toFixed(0)}`
                : tier.tier === "free" ? tier.price : "Unavailable";
              const paidTier = tier.tier !== "free";
              return (
            <div
              key={tier.name}
              className={`relative rounded-2xl border ${tier.borderColor} bg-[#0d1220] p-6 flex flex-col ${
                tier.popular ? "ring-2 ring-neon/50 shadow-lg shadow-neon/10" : ""
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-neon text-background text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                  Most Popular
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg ${tier.bgAccent} flex items-center justify-center`}>
                  <tier.icon className={`w-5 h-5 ${tier.color}`} />
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${tier.color}`}>{tier.name}</h3>
                </div>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-extrabold text-foreground">{displayPrice}</span>
                <span className="text-sm text-muted-foreground">{tier.period}</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{tier.description}</p>
              <div className="bg-navy-light/50 rounded-lg p-3 mb-6 border border-white/5">
                <p className="text-xs text-muted-foreground">Game Creation Fee</p>
                <p className={`text-lg font-bold ${tier.color}`}>
                  {tier.gameFee} <span className="text-xs font-normal text-muted-foreground">{tier.gameFeeNote}</span>
                </p>
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {tier.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.color}`} />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
              {paidTier ? (
                <Button
                  className={`w-full font-bold uppercase tracking-wide ${
                    tier.popular
                      ? "bg-neon hover:bg-neon-dim text-background"
                      : "bg-white/5 hover:bg-white/10 text-foreground border border-white/10"
                  }`}
                  onClick={() => beginCheckout(tier.tier as "starter" | "pro" | "enterprise")}
                  disabled={createSubscription.isPending || !stripePlan?.available}
                >
                  {checkoutTier === tier.tier && createSubscription.isPending ? "Opening secure checkout…" : tier.cta}
                </Button>
              ) : (
                <Link href={isAuthenticated ? tier.href : "/games"}>
                  <Button className="w-full font-bold uppercase tracking-wide bg-white/5 hover:bg-white/10 text-foreground border border-white/10">
                    {tier.cta}
                  </Button>
                </Link>
              )}
            </div>
              );
            })()
          ))}
        </div>
      </section>

      {/* Discount Triggers */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold uppercase mb-4">
              Ways to <span className="text-neon">Save</span> on Game Creation
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The $20 game creation fee exists so we can offer generous discounts that reward action.
              Most creators pay $5-$10 on average — and many create their first game for free.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {discountTriggers.map((d, i) => (
              <div key={i} className="feature-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-neon/10 flex items-center justify-center">
                    <d.icon className="w-5 h-5 text-neon" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-sm">{d.trigger}</h3>
                    <span className="text-xs font-bold text-neon">{d.discount}</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{d.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center uppercase mb-12">
            Pricing <span className="text-neon">FAQ</span>
          </h2>
          <div className="space-y-6">
            {[
              {
                q: "Is it free to play games?",
                a: "Yes. Playing games costs only the ticket price (entry fee) set by the game creator. There are no additional fees to participate. You also automatically get a promoter link with every ticket purchase.",
              },
              {
                q: "What is the 15% platform fee?",
                a: "The platform takes 15% of every game's total entry pool. This covers payment processing (credit card fees), infrastructure, customer support, and platform operations. The remaining 85% is split between the prize pool, creator share, and promoter pool.",
              },
              {
                q: "What is the game creation fee?",
                a: "The base game creation fee is $20 per game. However, most creators pay significantly less through discounts, subscriptions, and promotional offers. First-time creators get their first game free. The average effective fee is $5-$10.",
              },
              {
                q: "Can I be a creator and a promoter at the same time?",
                a: "Absolutely. You can create games and earn the creator share, while also promoting your own and other people's games to earn promoter commissions. Many of our most successful users do both.",
              },
              {
                q: "How does the promoter pool work?",
                a: "Every ticket buyer gets a unique referral link. If someone buys a ticket through your link, you earn a pro-rata share of the promoter pool. For example, if you sell 80 out of 100 tickets, you earn 80% of the promoter pool. No minimum — even selling 1 ticket earns you a share.",
              },
              {
                q: "Can I cancel my subscription anytime?",
                a: "Yes. All subscriptions are month-to-month with no long-term commitment. Cancel anytime and you'll retain access until the end of your billing period. Unused game creation credits do not roll over.",
              },
            ].map((item, i) => (
              <div key={i} className="glass-panel rounded-xl p-5">
                <h3 className="font-bold text-foreground mb-2">{item.q}</h3>
                <p className="text-sm text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold uppercase mb-4">
            Start <span className="text-neon">Earning</span> Today
          </h2>
          <p className="text-muted-foreground mb-8">
            Create your first game for free. No credit card required.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link href="/create">
              <Button className="bg-neon hover:bg-neon-dim text-background font-extrabold uppercase tracking-wide px-8 py-6 text-base">
                CREATE YOUR FIRST GAME FREE
              </Button>
            </Link>
            <Link href="/games">
              <Button variant="outline" className="border-neon/30 text-neon hover:bg-neon/10 font-bold uppercase tracking-wide px-8 py-6 text-base">
                BROWSE GAMES
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
